import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidV4 } from "uuid";

import styles from "./SinglePhotoDropzone.module.css";

// Dropzone
import { useDropzone } from "react-dropzone";
// Dropzone styles
import {
  baseStyle,
  focusedStyle,
  acceptStyle,
  rejectStyle,
} from "./DropzoneStyles";

// Helpers
import cloneDeep from "lodash.clonedeep";
import { ScrollDisable } from "../../helpers/ScrollLock";
import { getImageSrc, handleImageLoad } from "../../helpers/imageLoadUtils";
import toastManager from "../../helpers/toastManager";

// Custom components
import {
  SquareXButton,
  TextUtilityButton,
  DropzoneButton,
} from "../Buttons/Buttons";

const SinglePhotoDropzoneImage = ({
  props,
  file,
  imageLoading,
  setImageLoading,
}) => {
  const [imageSrc, setImageSrc] = useState("");
  useEffect(() => {
    setImageSrc(getImageSrc(file));
  }, []);

  return (
    <img
      src={imageSrc}
      draggable={false}
      className="object-scale-down
                 transition-all duration-200
                 cursor-pointer hover:scale-105 active:scale-100
                 w-full h-full
                 max-w-[25rem] max-h-[25rem]
                "
      onLoad={(e) =>
        handleImageLoad({
          imageElement: e.target,
          setItemLoading: setImageLoading(false),
        })
      }
      onClick={() => {
        ScrollDisable();
        props.setModalImage(file);
      }}
      style={
        !imageLoading
          ? {}
          : {
              position: "absolute",
              opacity: "0",
            }
      }
    />
  );
};

function SinglePhotoDropzone(props) {
  // Check if image is being processed by celery
  const isPendingOrProgress =
    props.calculationProgress?.state === "PENDING" ||
    props.calculationProgress?.state === "PROGRESS";

  const [imageLoading, setImageLoading] = useState(true);
  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    accept: {
      "image/*": [],
    },
    noClick: true,

    onDrop: (acceptedFiles) => {
      // console.log(acceptedFiles)
      if (!isPendingOrProgress) {
        if (acceptedFiles.length === 1) {
          const dataCopy = cloneDeep([...props.comparisonItems]);
          dataCopy[props.index][props.setFileKey] = [
            ...acceptedFiles.map((file, index) => {
              const uniqueKey = `${uuidV4()}-${index}`;

              return Object.assign(file, {
                uniqueKey: uniqueKey,
              });
            }),
          ];
          props.setComparisonItems(dataCopy);
        } else {
          toastManager(
            "warning",
            "Image Limit Exceeded!",
            `Please add a single image at a time to each comparison zone.`
          );
        }
      }

      // console.log(props.comparisonItems[props.index][props.setFileKey]);
    },
  });

  const removeFile = (file) => () => {
    const newFiles = [...props.comparisonItem[props.setFileKey]];
    newFiles.splice(newFiles.indexOf(file), 1);
    const dataCopy = cloneDeep([...props.comparisonItems]);
    dataCopy[props.index][props.setFileKey] = newFiles;
    props.setComparisonItems(dataCopy);
  };

  const removeAll = () => {
    const dataCopy = cloneDeep([...props.comparisonItems]);
    dataCopy[props.comparisonItem.index][props.setFileKey] = [];
    props.setComparisonItems(dataCopy);
  };

  const imageName = (file) => {
    if (file.previousEntryItem) {
      return file.file.name;
    } else if (file.apiItem) {
      return file.name;
    } else if (!file.apiItem) {
      return file.path;
    }
  };

  const figureName = { fileOne: "1", fileTwo: "2" };

  const thumbs = props.comparisonItem[props.setFileKey].map((file) => (
    <div
      key={file.uniqueKey}
      className="flex flex-col items-center justify-center h-full w-full p-2 Xbg-yellow-500"
    >
      <div
        className="flex flex-col items-center justify-between p-2 rounded-xl overflow-hidden Xbg-blue-500
                   outline outline-1
                   w-full h-full
                  "
      >
        <div className="flex flex-row items-center justify-end w-full Xbg-purple-500">
          <SquareXButton
            actionFunction={removeFile(file)}
            disabled={isPendingOrProgress}
          />
        </div>

        <div className="flex flex-1 flex-col items-center justify-between w-full Xbg-red-500">
          <div className="flex flex-col items-center justify-center h-full w-full p-5 md:p-0 Xbg-orange-500">
            {imageLoading && (
              <div className="flex flex-col items-center justify-center p-4 rounded-xl">
                <div className={styles["loader"]} />
              </div>
            )}

            <SinglePhotoDropzoneImage
              props={props}
              file={file}
              imageLoading={imageLoading}
              setImageLoading={setImageLoading}
            />
          </div>

          <div className="flex flex-col items-center justify-center w-full Xbg-green-500">
            <div className="flex flex-row items-center justify-start w-full py-1 Xbg-orange-500">
              <div className="flex flex-[3] flex-row items-center justify-start overflow-x-scroll">
                <p className="text-base font-normal text-neutral-900">
                  {imageName(file)}
                </p>
              </div>
              <div className="flex flex-[2] flex-row items-center justify-end">
                <p className="text-base font-normal text-neutral-900">
                  {file.previousEntryItem ? (
                    <>{Math.round((file.file.size / 1000000) * 100) / 100} MB</>
                  ) : (
                    <>{Math.round((file.size / 1000000) * 100) / 100} MB</>
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-row w-full">
              <input
                className="flex w-full items-center p-2 border border-neutral-300 rounded-lg
                           focus:outline-none focus:ring-inset focus:ring-neutral-300
                           text-base font-normal text-neutral-900
                           "
                placeholder={`Figure ${props.index + 1}.${
                  figureName[props.setFileKey]
                }.`}
                value={
                  props.comparisonItems[props.index][
                    `${props.setFileKey}Details`
                  ].caption
                }
                onChange={(event) => {
                  const dataCopy = cloneDeep(props.comparisonItems);
                  dataCopy[props.index][`${props.setFileKey}Details`].caption =
                    event.target.value;
                  props.setComparisonItems(dataCopy);
                }}
                maxLength={50}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  ));

  // Dynamically change style of dropzone depending on what is happening
  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  useEffect(() => {
    // Runs on unmount. Revoke the uris to avoid memory leaks
    return () =>
      props.comparisonItem[props.setFileKey].forEach((file) =>
        URL.revokeObjectURL(file.preview)
      );
  }, []);

  return (
    <div className="flex flex-1 flex-grow flex-col items-center justify-center w-full h-full Xbg-pink-500">
      <div className="flex flex-row items-start justify-between w-full h-16 Xbg-green-500">
        <TextUtilityButton
          prompt={"Browse Entries"}
          customFont={"lg:text-xl font-medium"}
          actionFunction={() => {
            props.setComparisonItemLocation({
              index: props.index,
              fileKey: props.setFileKey,
            });

            props.setEntryBrowserModalOpen(true);
            ScrollDisable();
          }}
          disabled={isPendingOrProgress || props.rapidEntry}
        />

        <TextUtilityButton
          prompt={"Select Images"}
          customFont={"lg:text-xl font-medium"}
          actionFunction={open}
          disabled={isPendingOrProgress}
        />
      </div>

      <div {...getRootProps({ className: "dropzone", style })}>
        <input {...getInputProps()} />

        {props.comparisonItem[props.setFileKey].length === 0 && (
          <div className="mb-10">
            {/* mb-10 is to align processing dropzone icons */}
            <DropzoneButton openFunction={open} />
          </div>
        )}

        {thumbs.length > 0 && (
          <div className="flex flex-row flex-wrap items-center justify-center w-full h-full">
            {thumbs}
          </div>
        )}
      </div>
    </div>
  );
}

export default SinglePhotoDropzone;
